import React, { useState } from 'react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import FadeInSection from '@/components/ui/FadeInSection';
import GlassCard from '@/components/ui/GlassCard';
import { Button } from '@/components/ui/button';
import { 
  Globe, 
  Fingerprint, 
  UserCheck, 
  BrainCircuit, 
  Users, 
  Scale, 
  SmartphoneNfc,
  Check, 
  ShoppingCart, 
  DollarSign, 
  Stethoscope, 
  PlaySquare, 
  AppWindow, 
  Gamepad,
  TrendingUp,
  Code,
  Lock,
  UserCog,
  Languages,
  Zap,
  Coins,
  BarChart3,
  LineChart,
  PieChart,
  Activity,
  Shield,
  X,
  ZoomIn
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogTrigger, ImageDialogContent } from "@/components/ui/dialog";

const CIAM = () => {
  const [activeImage, setActiveImage] = useState<string | null>(null);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-6">
          {/* Hero Section */}
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="inline-block rounded-full px-3 py-1 text-sm bg-[#b688a4]/10 text-[#b688a4] font-medium mb-4">
                CUSTOMER IDENTITY
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Powering Customer Identity, Together</h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto">
                Smart, adaptive, and friction-free identity.
              </p>
            </div>
          </FadeInSection>

          {/* Introduction */}
          <FadeInSection delay={100}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-20">
              <div>
                <h2 className="text-3xl font-bold mb-6">Elevate Customer Experience with Secure Identity</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Our team is side by side with your business, we make a difference. Designed to simplify identity management, it enables passwordless authentication, social login, adaptive security, and real-time telemetry, ensuring customers enjoy effortless access while their data remains protected. With enterprise-grade compliance and intuitive integrations, Keystle empowers businesses to build trust and scale securely.
                </p>
                <Button 
                  className="bg-[#b688a4] hover:bg-[#b688a4]/90 text-white"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  Get Started
                </Button>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/lovable-uploads/a72b2510-ff0d-460b-bc94-4003c140c209.png" 
                  alt="Digital identity management with holographic interface" 
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  width="600" 
                  height="400"
                />
              </div>
            </div>
          </FadeInSection>

          {/* Key Features Section */}
          <FadeInSection delay={200}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Key Features</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive identity and access management solutions designed for customer-facing applications.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Passwordless Authentication */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Fingerprint className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Passwordless Authentication</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Biometric authentication</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>WebAuthn passkeys</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>QR code login</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Magic links via email</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* Social & Federated Login */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <UserCheck className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Social & Federated Login</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Google, Apple, Facebook integration</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>One-click registration</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Custom identity provider support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Simplified user onboarding</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* Real-Time Telemetry & Insights */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <BrainCircuit className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Real-Time Telemetry & Insights</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>User behavior analytics</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Authentication trend monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Security threat detection</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Customizable dashboards</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* Privacy-First Compliance */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Scale className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Privacy-First Requirements</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>GDPR & CCPA requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>LGPD & HIPAA frameworks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Consent management</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#b688a4] mr-2 mt-1">•</span>
                      <span>Data privacy controls</span>
                    </li>
                  </ul>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Additional Features Section */}
          <FadeInSection delay={300}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Additional Features</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Enhanced capabilities to deliver superior customer identity experiences.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Multi-Factor Authentication */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Lock className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Multi-Factor Authentication</h3>
                  <p className="text-muted-foreground mb-4">
                    Expanded security with App OTP, email verification, and adaptive authentication that adjusts based on risk factors for enhanced protection.
                  </p>
                </GlassCard>

                {/* Scalable Multi-Tenant Support */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Users className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Multi-Tenant Architecture</h3>
                  <p className="text-muted-foreground mb-4">
                    Secure identity management across multiple brands, regions, or business units with centralized visibility and segmented data storage.
                  </p>
                </GlassCard>

                {/* Omnichannel Authentication */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <SmartphoneNfc className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Omnichannel Experience</h3>
                  <p className="text-muted-foreground mb-4">
                    Consistent, secure authentication across mobile apps, web platforms, IoT devices, and enterprise applications with seamless session management.
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Use Cases */}
          <FadeInSection delay={400}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Use Cases</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Real-world applications that deliver tangible benefits across industries.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* E-Commerce */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <ShoppingCart className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">E-Commerce & Marketplaces</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Secure, seamless checkout authentication</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Reduced cart abandonment with frictionless login</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Customer profile management for personalization</span>
                    </li>
                  </ul>
                </div>

                {/* Fintech & Banking */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <DollarSign className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Fintech & Banking</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Strengthened security with adaptive authentication</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>AI-powered fraud prevention and detection</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Regulatory compliance with industry standards</span>
                    </li>
                  </ul>
                </div>

                {/* Healthcare & Telemedicine */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <Stethoscope className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Healthcare & Telemedicine</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Protection of sensitive patient data</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>HIPAA-compliant authentication flows</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Secure telehealth user verification</span>
                    </li>
                  </ul>
                </div>

                {/* Media & Entertainment */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <PlaySquare className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Media & Entertainment</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Fast, secure access to streaming services</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Digital subscription management</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Cross-device content access control</span>
                    </li>
                  </ul>
                </div>

                {/* B2C SaaS & Consumer Apps */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <AppWindow className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">B2C SaaS & Consumer Apps</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Customizable identity experiences</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Multi-tenant authentication architecture</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Granular user access control</span>
                    </li>
                  </ul>
                </div>

                {/* Gaming & Esports */}
                <div className="bg-gradient-to-br from-background to-muted rounded-lg p-6 shadow-lg">
                  <div className="text-[#b688a4] mb-4">
                    <Gamepad className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Gaming & Esports</h3>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Enhanced player security and experience</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Fraud detection for fair gameplay</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Account takeover prevention</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Differentiators Section */}
          <FadeInSection delay={500}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Advanced Features</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Redefining access with trust and innovation.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Frictionless Sign-In */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Zap className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Frictionless Sign-In</h3>
                  <p className="text-muted-foreground mb-4">
                    Eliminate login barriers with passwordless authentication and social login options that reduce friction while maintaining high security standards.
                  </p>
                </GlassCard>

                {/* User-Controlled Consent & Privacy */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <UserCog className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">User-Controlled Privacy</h3>
                  <p className="text-muted-foreground mb-4">
                    Empower users with transparent data privacy controls and preference management, building trust through clear consent mechanisms.
                  </p>
                </GlassCard>

                {/* Localization & Multi-Language Support */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Languages className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Multi-Language Support</h3>
                  <p className="text-muted-foreground mb-4">
                    Provide seamless authentication experiences tailored to global user bases with comprehensive localization and language options.
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>
          
          {/* Business Efficiency Section */}
          <FadeInSection delay={600}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Business Efficiency & Scalability</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Enterprise-grade performance with flexible implementation options.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* High-Performance Authentication */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <TrendingUp className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">High-Performance Auth</h3>
                  <p className="text-muted-foreground mb-4">
                    Handle millions of logins per second with low latency and high availability, ensuring consistent performance during traffic spikes.
                  </p>
                </GlassCard>

                {/* Flexible, Usage-Based Pricing */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Coins className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Usage-Based Pricing</h3>
                  <p className="text-muted-foreground mb-4">
                    Transparent cost models tailored for B2C enterprises and SaaS platforms, with predictable pricing that scales with your business needs.
                  </p>
                </GlassCard>

                {/* Developer-First APIs */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Code className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Developer-First APIs</h3>
                  <div className="flex items-center mb-3">
                    <span className="rounded-full px-2 py-0.5 text-xs bg-[#b688a4]/20 text-[#b688a4] font-medium mr-2">
                      Coming Soon
                    </span>
                    <span className="text-sm text-muted-foreground">SDK</span>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Comprehensive integration tools and documentation for fast deployment, with SDKs for all major platforms and frameworks.
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>
          
          {/* Real-Time Identity Section */}
          <FadeInSection delay={650}>
            <div className="mb-20">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Real-Time Identity Intelligence</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Actionable insights and analytics to enhance security and user experience.
                </p>
              </div>
              
              {/* Dashboard Images with Zoom Feature */}
              <div className="flex flex-col md:flex-row gap-4 mb-10 justify-center">
                <div className="relative group cursor-pointer">
                  <img 
                    src="/lovable-uploads/d3c15c14-cdb3-4ef0-991c-cfefd50eab07.png" 
                    alt="Customer Identity Dashboard" 
                    className="rounded-lg shadow-md w-full md:max-w-full transition-all duration-300 group-hover:opacity-95"
                  />
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30 rounded-lg">
                        <div className="bg-white/90 p-2 rounded-full">
                          <ZoomIn className="h-8 w-8 text-[#b688a4]" />
                        </div>
                      </div>
                    </DialogTrigger>
                    <ImageDialogContent className="max-w-7xl">
                      <div className="relative">
                        <img 
                          src="/lovable-uploads/d3c15c14-cdb3-4ef0-991c-cfefd50eab07.png" 
                          alt="Customer Identity Dashboard - Expanded View" 
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    </ImageDialogContent>
                  </Dialog>
                </div>
                
                <div className="relative group cursor-pointer">
                  <img 
                    src="/lovable-uploads/eeff177a-5c98-4235-abf2-66e9fd3b108c.png" 
                    alt="Authentication Analytics" 
                    className="rounded-lg shadow-md w-full md:max-w-full transition-all duration-300 group-hover:opacity-95"
                  />
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30 rounded-lg">
                        <div className="bg-white/90 p-2 rounded-full">
                          <ZoomIn className="h-8 w-8 text-[#b688a4]" />
                        </div>
                      </div>
                    </DialogTrigger>
                    <ImageDialogContent className="max-w-7xl">
                      <div className="relative">
                        <img 
                          src="/lovable-uploads/eeff177a-5c98-4235-abf2-66e9fd3b108c.png" 
                          alt="Authentication Analytics - Expanded View" 
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    </ImageDialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Identity Analytics */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <BarChart3 className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Identity Analytics</h3>
                  <p className="text-muted-foreground mb-4">
                    Comprehensive dashboards that visualize authentication patterns, user behavior, and conversion metrics to optimize your identity strategy.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Login success rate monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Authentication method adoption</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* Threat Detection */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Shield className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Threat Detection</h3>
                  <p className="text-muted-foreground mb-4">
                    AI-powered security that identifies suspicious activities and potential account takeover attempts in real-time before damage occurs.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Anomaly detection algorithms</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Risk-based authentication</span>
                    </li>
                  </ul>
                </GlassCard>

                {/* User Journey Optimization */}
                <GlassCard className="p-6">
                  <div className="text-[#b688a4] mb-4">
                    <Activity className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">User Journey Insights</h3>
                  <p className="text-muted-foreground mb-4">
                    Track and analyze the complete authentication journey to identify friction points and streamline the user experience.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Conversion funnel analysis</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-[#b688a4] mr-2 mt-0.5 flex-shrink-0" />
                      <span>Drop-off point identification</span>
                    </li>
                  </ul>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* CTA Section */}
          <FadeInSection delay={800}>
            <div className="text-center py-16 px-6 bg-gradient-to-r from-[#b688a4]/10 to-transparent rounded-2xl">
              <h2 className="text-3xl font-bold mb-4">Ready to transform your customer identity management?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Discover how our CIAM solutions can enhance your customer experiences while strengthening security.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  className="bg-[#b688a4] hover:bg-[#b688a4]/90 text-white"
                  size="lg"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CIAM;
